import React, { useState, useEffect } from 'react'
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonButton,
  IonIcon,
  IonList,
  IonItem,
  IonLabel,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonChip,
  IonBadge,
  IonAlert,
  IonToast,
  IonGrid,
  IonRow,
  IonCol,
  IonInput,
  IonTextarea,
  IonSelect,
  IonSelectOption,
  IonImg,
  IonSpinner,

  IonFab,
  IonFabButton,
  IonActionSheet,
  IonSearchbar
} from '@ionic/react'
import {
  mailOutline,
  addOutline,
  closeOutline,
  qrCodeOutline,
  checkmarkCircleOutline,
  timeOutline,
  trashOutline,
  copyOutline,
  eyeOutline,
  createOutline,
  shareOutline,
  linkOutline,
  personAddOutline,
  warningOutline,
  chevronBackOutline,
  chevronForwardOutline,
  playBackOutline,
  playForwardOutline,
  searchOutline,
  personOutline,
  informationCircleOutline,
  listOutline,
  settingsOutline
} from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../store/authStore'
import { supabase } from '../services/supabase'
import QRCode from 'qrcode'

interface EnhancedInvitationCode {
  id: string
  coach_id: string
  code: string
  created_at: string
  expires_at: string
  used: boolean
  used_by?: string
  used_at?: string
  max_uses: number
  current_uses: number
  coachee_first_name?: string
  coachee_last_name?: string
  coachee_email?: string
  status: 'pending' | 'accepted' | 'expired' | 'revoked'
  notes?: string
}

interface AcceptedCoacheeInfo {
  id: string
  full_name: string
  email: string
  avatar_url?: string
}

interface InvitationFormData {
  coachee_first_name: string
  coachee_last_name: string
  coachee_email: string
  notes: string
  max_uses: number
}

const InvitationManagement: React.FC = () => {
  const { user } = useAuthStore()
  const { t } = useTranslation()
  
  const [invitations, setInvitations] = useState<EnhancedInvitationCode[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedInvitation, setSelectedInvitation] = useState<EnhancedInvitationCode | null>(null)
  const [acceptedCoacheeInfo, setAcceptedCoacheeInfo] = useState<AcceptedCoacheeInfo | null>(null)
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('')
  const [isCreatingInvitation, setIsCreatingInvitation] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [showRevokeAlert, setShowRevokeAlert] = useState(false)
  const [invitationToRevoke, setInvitationToRevoke] = useState<string | null>(null)
  const [showDeleteAlert, setShowDeleteAlert] = useState(false)
  const [invitationToDelete, setInvitationToDelete] = useState<string | null>(null)
  const [showActionSheet, setShowActionSheet] = useState(false)
  const [actionSheetInvitation, setActionSheetInvitation] = useState<EnhancedInvitationCode | null>(null)
  const [isEditingDetails, setIsEditingDetails] = useState(false)
  const [isSavingDetails, setIsSavingDetails] = useState(false)

  
  const [formData, setFormData] = useState<InvitationFormData>({
    coachee_first_name: '',
    coachee_last_name: '',
    coachee_email: '',
    notes: '',
    max_uses: 1
  })

  const [editDetailsData, setEditDetailsData] = useState({
    coachee_first_name: '',
    coachee_last_name: '',
    coachee_email: '',
    notes: ''
  })

  // Search, filter, and pagination state
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(5)

  useEffect(() => {
    if (user) {
      fetchInvitations()
      
      // Set up real-time subscription for invitation changes
      const subscription = supabase
        .channel('invitation_codes_changes')
        .on('postgres_changes', 
          { 
            event: '*', 
            schema: 'public', 
            table: 'invitation_codes',
            filter: `coach_id=eq.${user.id}`
          }, 
          (payload) => {
            console.log('Invitation code changed:', payload)
            // Refresh invitations when any change occurs
            fetchInvitations()
            
            // Show a subtle notification for status changes
            if (payload.eventType === 'UPDATE' && payload.new?.status !== payload.old?.status) {
              setToastMessage(`Invitation status updated to "${payload.new?.status}"`)
              setShowToast(true)
            }
          }
        )
        .subscribe()

      return () => {
        subscription.unsubscribe()
      }
    }
  }, [user])

  const fetchInvitations = async () => {
    try {
      setLoading(true)
      const { data, error } = await supabase
        .from('invitation_codes')
        .select('*')
        .eq('coach_id', user!.id)
        .order('created_at', { ascending: false })

      if (error) throw error

      // Update status for expired invitations in the database
      const now = new Date()
      const expiredInvitations = data?.filter(invitation => 
        new Date(invitation.expires_at) < now && invitation.status === 'pending'
      ) || []

      // Update expired invitations in the database
      if (expiredInvitations.length > 0) {
        const { error: updateError } = await supabase
          .from('invitation_codes')
          .update({ status: 'expired' })
          .in('id', expiredInvitations.map(inv => inv.id))

        if (updateError) {
          console.error('Error updating expired invitations:', updateError)
        }
      }

      // Apply status updates to the local data
      const updatedInvitations = data?.map(invitation => {
        if (new Date(invitation.expires_at) < now && invitation.status === 'pending') {
          return { ...invitation, status: 'expired' as const }
        }
        return invitation
      }) || []

      setInvitations(updatedInvitations)
    } catch (error) {
      console.error('Error fetching invitations:', error)
      setToastMessage('Failed to load invitations')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const generateQRCode = async (code: string): Promise<string> => {
    try {
      // Create a registration URL with the invitation code
      const registrationUrl = `${window.location.origin}/register?code=${code}`
      const qrDataUrl = await QRCode.toDataURL(registrationUrl, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      })
      return qrDataUrl
    } catch (error) {
      console.error('Error generating QR code:', error)
      return ''
    }
  }

  const createInvitation = async () => {
    if (!formData.coachee_first_name.trim() || !formData.coachee_last_name.trim() || !formData.coachee_email.trim()) {
      setToastMessage('Please fill in all required fields')
      setShowToast(true)
      return
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.coachee_email)) {
      setToastMessage('Please enter a valid email address')
      setShowToast(true)
      return
    }

    setIsCreatingInvitation(true)
    try {
      // Generate a unique code
      const code = `COACH-${new Date().getFullYear()}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`
      
      // Save to database
      const { data, error } = await supabase
        .from('invitation_codes')
        .insert([{
          coach_id: user!.id,
          code,
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          max_uses: formData.max_uses,
          current_uses: 0,
          coachee_first_name: formData.coachee_first_name.trim(),
          coachee_last_name: formData.coachee_last_name.trim(),
          coachee_email: formData.coachee_email.trim().toLowerCase(),
          notes: formData.notes.trim() || null,
          status: 'pending'
        }])
        .select()
        .single()

      if (error) throw error

      setInvitations(prev => [data, ...prev])
      setToastMessage(`Invitation created for ${formData.coachee_first_name} ${formData.coachee_last_name}`)
      setShowToast(true)
      setShowCreateModal(false)
      
      // Reset form
      setFormData({
        coachee_first_name: '',
        coachee_last_name: '',
        coachee_email: '',
        notes: '',
        max_uses: 1
      })
    } catch (error) {
      console.error('Error creating invitation:', error)
      setToastMessage('Failed to create invitation')
      setShowToast(true)
    } finally {
      setIsCreatingInvitation(false)
    }
  }

  const revokeInvitation = async () => {
    if (!invitationToRevoke) return

    try {
      const { error } = await supabase
        .from('invitation_codes')
        .update({ status: 'revoked' })
        .eq('id', invitationToRevoke)

      if (error) throw error

      setInvitations(prev => 
        prev.map(inv => 
          inv.id === invitationToRevoke 
            ? { ...inv, status: 'revoked' as const }
            : inv
        )
      )

      setToastMessage('Invitation revoked successfully')
      setShowToast(true)
      setShowRevokeAlert(false)
      setInvitationToRevoke(null)
    } catch (error) {
      console.error('Error revoking invitation:', error)
      setToastMessage('Failed to revoke invitation')
      setShowToast(true)
    }
  }

  const deleteInvitation = async () => {
    if (!invitationToDelete) return

    try {
      const { error } = await supabase
        .from('invitation_codes')
        .delete()
        .eq('id', invitationToDelete)

      if (error) throw error

      // Remove from local state
      setInvitations(prev => prev.filter(inv => inv.id !== invitationToDelete))

      // Close details modal if the deleted invitation was being viewed
      if (selectedInvitation?.id === invitationToDelete) {
        setShowDetailsModal(false)
        setSelectedInvitation(null)
      }

      setToastMessage('Invitation deleted successfully')
      setShowToast(true)
      setShowDeleteAlert(false)
      setInvitationToDelete(null)
    } catch (error) {
      console.error('Error deleting invitation:', error)
      setToastMessage('Failed to delete invitation')
      setShowToast(true)
    }
  }

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setToastMessage(`${label} copied to clipboard!`)
      setShowToast(true)
    })
  }

  const shareInvitation = async (invitation: EnhancedInvitationCode) => {
    const registrationUrl = `${window.location.origin}/register?code=${invitation.code}`
    const shareText = `Hi ${invitation.coachee_first_name},\n\nYou've been invited to join my coaching program on InsightFlow.\n\nUse this invitation code: ${invitation.code}\n\nOr register directly: ${registrationUrl}\n\nLooking forward to working with you!`
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'InsightFlow Coaching Invitation',
          text: shareText,
          url: registrationUrl
        })
      } catch (error) {
        // Fallback to clipboard
        copyToClipboard(shareText, 'Invitation message')
      }
    } else {
      copyToClipboard(shareText, 'Invitation message')
    }
  }

  const openInvitationDetails = async (invitation: EnhancedInvitationCode) => {
    setSelectedInvitation(invitation)
    setAcceptedCoacheeInfo(null) // Reset previous data
    
    // Set edit form data
    setEditDetailsData({
      coachee_first_name: invitation.coachee_first_name || '',
      coachee_last_name: invitation.coachee_last_name || '',
      coachee_email: invitation.coachee_email || '',
      notes: invitation.notes || ''
    })
    
    // If invitation is accepted, fetch the actual coachee information
    if (invitation.status === 'accepted' && invitation.used_by) {
      try {
        const { data: coacheeData, error } = await supabase
          .from('profiles')
          .select('id, full_name, email, avatar_url')
          .eq('id', invitation.used_by)
          .single()

        if (!error && coacheeData) {
          setAcceptedCoacheeInfo(coacheeData)
        }
      } catch (error) {
        console.error('Error fetching accepted coachee info:', error)
      }
    }
    
    // Generate QR code
    const qrCode = await generateQRCode(invitation.code)
    setQrCodeDataUrl(qrCode)
    
    setShowDetailsModal(true)
    setIsEditingDetails(false) // Reset editing state
  }

  const saveInvitationDetails = async () => {
    if (!selectedInvitation) return

    // Basic validation
    if (!editDetailsData.coachee_first_name.trim() || !editDetailsData.coachee_last_name.trim() || !editDetailsData.coachee_email.trim()) {
      setToastMessage('Please fill in all required fields')
      setShowToast(true)
      return
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(editDetailsData.coachee_email)) {
      setToastMessage('Please enter a valid email address')
      setShowToast(true)
      return
    }

    setIsSavingDetails(true)
    try {
      const { error } = await supabase
        .from('invitation_codes')
        .update({
          coachee_first_name: editDetailsData.coachee_first_name.trim(),
          coachee_last_name: editDetailsData.coachee_last_name.trim(),
          coachee_email: editDetailsData.coachee_email.trim().toLowerCase(),
          notes: editDetailsData.notes.trim() || null
        })
        .eq('id', selectedInvitation.id)

      if (error) throw error

      // Update local state
      const updatedInvitation: EnhancedInvitationCode = {
        ...selectedInvitation,
        coachee_first_name: editDetailsData.coachee_first_name.trim(),
        coachee_last_name: editDetailsData.coachee_last_name.trim(),
        coachee_email: editDetailsData.coachee_email.trim().toLowerCase(),
        notes: editDetailsData.notes.trim() || undefined
      }

      setSelectedInvitation(updatedInvitation)
      setInvitations(prev => 
        prev.map(inv => 
          inv.id === selectedInvitation.id ? updatedInvitation : inv
        )
      )

      setToastMessage('Invitation details updated successfully')
      setShowToast(true)
      setIsEditingDetails(false)
    } catch (error) {
      console.error('Error updating invitation details:', error)
      setToastMessage('Failed to update invitation details')
      setShowToast(true)
    } finally {
      setIsSavingDetails(false)
    }
  }

  const cancelEditDetails = () => {
    if (!selectedInvitation) return
    
    // Reset to original values
    setEditDetailsData({
      coachee_first_name: selectedInvitation.coachee_first_name || '',
      coachee_last_name: selectedInvitation.coachee_last_name || '',
      coachee_email: selectedInvitation.coachee_email || '',
      notes: selectedInvitation.notes || ''
    })
    setIsEditingDetails(false)
  }



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'success'
      case 'pending': return 'warning'
      case 'expired': return 'medium'
      case 'revoked': return 'danger'
      default: return 'medium'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted': return checkmarkCircleOutline
      case 'pending': return timeOutline
      case 'expired': return warningOutline
      case 'revoked': return closeOutline
      default: return timeOutline
    }
  }

  const handleInvitationAction = (invitation: EnhancedInvitationCode) => {
    setActionSheetInvitation(invitation)
    setShowActionSheet(true)
  }

  // Filter and pagination logic
  const filteredInvitations = invitations.filter(invitation => {
    const matchesSearch = 
      invitation.coachee_first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invitation.coachee_last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invitation.coachee_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invitation.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invitation.notes?.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesStatus = statusFilter === 'all' || invitation.status === statusFilter

    return matchesSearch && matchesStatus
  })

  // Get only active/pending invitations for the "Invitation Codes" section
  const activeInvitations = invitations.filter(invitation => invitation.status === 'pending')

  const totalPages = Math.ceil(filteredInvitations.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedInvitations = filteredInvitations.slice(startIndex, startIndex + itemsPerPage)

  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
    setCurrentPage(1) // Reset to first page when searching
  }

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status)
    setCurrentPage(1) // Reset to first page when filtering
  }

  const handlePageSizeChange = (newSize: number) => {
    setItemsPerPage(newSize)
    setCurrentPage(1) // Reset to first page when changing page size
  }

  return (
    <>
      <IonCard>
        <IonCardHeader>
          <IonCardTitle className="flex items-center justify-between text-base sm:text-lg">
            <div className="flex items-center">
              <IonIcon icon={linkOutline} className="mr-2 flex-shrink-0" />
              {t('dashboard.coach.invitationCodes', 'Invitation Codes')}
            </div>
            <div className="flex items-center space-x-2">
              <IonButton 
                fill="clear" 
                size="small"
                onClick={() => setShowCreateModal(true)}
              >
                <IonIcon icon={addOutline} />
                <span className="hidden sm:inline ml-2">{t('invitations.create', 'Create')}</span>
              </IonButton>
            </div>
          </IonCardTitle>
        </IonCardHeader>
        <IonCardContent>
          {/* Search and Filter Controls */}
          <div className="mb-4 space-y-3">
            <IonSearchbar
              value={searchQuery}
              onIonInput={(e: CustomEvent) => handleSearchChange(e.detail.value!)}
              placeholder={t('invitations.search', 'Search invitations...')}
              debounce={300}
            />
            
            <div className="flex items-center space-x-2">
              <IonSelect
                value={statusFilter}
                onIonChange={(e) => handleStatusFilterChange(e.detail.value)}
                placeholder={t('invitations.filterStatus', 'Filter by status')}
                interface="popover"
                className="flex-1"
              >
                <IonSelectOption value="all">{t('invitations.statusAll', 'All Statuses')}</IonSelectOption>
                <IonSelectOption value="pending">{t('invitations.statusPending', 'Pending')}</IonSelectOption>
                <IonSelectOption value="accepted">{t('invitations.statusAccepted', 'Accepted')}</IonSelectOption>
                <IonSelectOption value="expired">{t('invitations.statusExpired', 'Expired')}</IonSelectOption>
                <IonSelectOption value="revoked">{t('invitations.statusRevoked', 'Revoked')}</IonSelectOption>
              </IonSelect>
              
              {(searchQuery || statusFilter !== 'all') && (
                <IonButton 
                  fill="clear" 
                  size="small"
                  onClick={() => {
                    setSearchQuery('')
                    setStatusFilter('all')
                    setCurrentPage(1)
                  }}
                >
                  <IonIcon icon={closeOutline} />
                </IonButton>
              )}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <IonSpinner />
            </div>
          ) : filteredInvitations.length > 0 ? (
            <>
              <IonList>
                {paginatedInvitations.map((invitation) => (
                  <IonItem key={invitation.id} button onClick={() => openInvitationDetails(invitation)}>
                    <div className="flex items-start w-full pl-0 pr-4 py-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h3 className="font-medium">
                            {invitation.coachee_first_name} {invitation.coachee_last_name}
                          </h3>
                          <IonChip color={getStatusColor(invitation.status)} className="text-xs ml-2">
                            <IonIcon icon={getStatusIcon(invitation.status)} />
                            <IonLabel>{invitation.status}</IonLabel>
                          </IonChip>
                        </div>
                        <p className="text-sm text-gray-600" style={{ 
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word'
                        }}>
                          {invitation.coachee_email?.replace('@', '\u200B@') || ''}
                        </p>
                        <div className="mt-1">
                          <span className="text-xs text-gray-500">Code: </span>
                          <span className="text-xs text-gray-800 font-mono">
                            {invitation.code}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500">
                          Created: {new Date(invitation.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </IonItem>
                ))}
              </IonList>

              {/* Pagination Controls */}
              <div className="flex flex-col space-y-4 mt-4">
                {/* Items per page selector and results info */}
                <div className="flex flex-nowrap items-center justify-between text-sm">
                  <div className="flex items-center space-x-1">
                    <span className="text-gray-600">{t('common.show', 'Show')}:</span>
                    <IonSelect
                      value={itemsPerPage}
                      onIonChange={(e) => handlePageSizeChange(e.detail.value)}
                      interface="popover"
                      className="w-16"
                    >
                      <IonSelectOption value={3}>3</IonSelectOption>
                      <IonSelectOption value={5}>5</IonSelectOption>
                      <IonSelectOption value={10}>10</IonSelectOption>
                      <IonSelectOption value={25}>25</IonSelectOption>
                      <IonSelectOption value={50}>50</IonSelectOption>
                    </IonSelect>
                  </div>
                  <div className="text-gray-600">
                    {t('common.showing', 'Showing')} {startIndex + 1}-{Math.min(startIndex + itemsPerPage, filteredInvitations.length)} {t('common.of', 'of')} {filteredInvitations.length}
                  </div>
                </div>

                {/* Navigation controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between">
                    {/* Desktop navigation - full text */}
                    <div className="hidden sm:flex items-center space-x-2">
                      <IonButton
                        fill="clear"
                        size="small"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(1)}
                        className="min-w-[60px]"
                      >
                        <IonIcon icon={playBackOutline} slot="start" />
                        {t('common.first', 'First')}
                      </IonButton>
                      <IonButton
                        fill="clear"
                        size="small"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                        className="min-w-[80px]"
                      >
                        <IonIcon icon={chevronBackOutline} slot="start" />
                        {t('common.previous', 'Previous')}
                      </IonButton>
                    </div>

                    {/* Mobile navigation - icons only */}
                    <div className="flex sm:hidden items-center space-x-1">
                      <IonButton
                        fill="clear"
                        size="small"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(1)}
                      >
                        <IonIcon icon={playBackOutline} />
                      </IonButton>
                      <IonButton
                        fill="clear"
                        size="small"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        <IonIcon icon={chevronBackOutline} />
                      </IonButton>
                    </div>

                    {/* Page indicator */}
                    <div className="flex items-center">
                      <span className="text-sm text-gray-600 hidden sm:block">
                        {t('common.page', 'Page')} {currentPage} {t('common.of', 'of')} {totalPages}
                      </span>
                      <span className="text-sm text-gray-600 sm:hidden">
                        {currentPage}/{totalPages}
                      </span>
                    </div>

                    {/* Desktop navigation - full text */}
                    <div className="hidden sm:flex items-center space-x-2">
                      <IonButton
                        fill="clear"
                        size="small"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                        className="min-w-[60px]"
                      >
                        {t('common.next', 'Next')}
                        <IonIcon icon={chevronForwardOutline} slot="end" />
                      </IonButton>
                      <IonButton
                        fill="clear"
                        size="small"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        className="min-w-[60px]"
                      >
                        {t('common.last', 'Last')}
                        <IonIcon icon={playForwardOutline} slot="end" />
                      </IonButton>
                    </div>

                    {/* Mobile navigation - icons only */}
                    <div className="flex sm:hidden items-center space-x-1">
                      <IonButton
                        fill="clear"
                        size="small"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        <IonIcon icon={chevronForwardOutline} />
                      </IonButton>
                      <IonButton
                        fill="clear"
                        size="small"
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                      >
                        <IonIcon icon={playForwardOutline} />
                      </IonButton>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : invitations.length > 0 ? (
            <div className="text-center py-8">
              <IonIcon icon={searchOutline} className="text-4xl text-gray-400 mb-4" />
              <p className="text-gray-500 mb-4">{t('invitations.noResults', 'No invitations match your search criteria')}</p>
              <IonButton 
                fill="outline" 
                size="small"
                onClick={() => {
                  setSearchQuery('')
                  setStatusFilter('all')
                  setCurrentPage(1)
                }}
              >
                {t('common.clearFilters', 'Clear Filters')}
              </IonButton>
            </div>
          ) : (
            <div className="text-center py-8">
              <IonIcon icon={mailOutline} className="text-4xl text-gray-400 mb-4 hidden sm:block" />
              <p className="text-gray-500 mb-4">{t('invitations.empty', 'No invitations created yet')}</p>
              <IonButton 
                fill="outline" 
                size="small"
                onClick={() => setShowCreateModal(true)}
              >
                <IonIcon icon={personAddOutline} />
                <span className="hidden sm:inline ml-2">{t('invitations.createFirst', 'Create Your First Invitation')}</span>
                <span className="sm:hidden ml-2">{t('invitations.create', 'Create')}</span>
              </IonButton>
            </div>
          )}
        </IonCardContent>
      </IonCard>

      {/* Create Invitation Modal */}
      <IonModal isOpen={showCreateModal} onDidDismiss={() => setShowCreateModal(false)}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{t('invitations.createTitle', 'Create Invitation')}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowCreateModal(false)}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div className="space-y-4">
            <IonItem className="rounded-lg">
              <IonLabel position="stacked">
                {t('invitations.form.firstName', 'First Name')} *
              </IonLabel>
              <IonInput
                value={formData.coachee_first_name}
                placeholder={t('invitations.form.firstNamePlaceholder', 'Enter first name')}
                onIonInput={(e) => setFormData(prev => ({ ...prev, coachee_first_name: e.detail.value! }))}
              />
            </IonItem>

            <IonItem className="rounded-lg">
              <IonLabel position="stacked">
                {t('invitations.form.lastName', 'Last Name')} *
              </IonLabel>
              <IonInput
                value={formData.coachee_last_name}
                placeholder={t('invitations.form.lastNamePlaceholder', 'Enter last name')}
                onIonInput={(e) => setFormData(prev => ({ ...prev, coachee_last_name: e.detail.value! }))}
              />
            </IonItem>

            <IonItem className="rounded-lg">
              <IonLabel position="stacked">
                {t('invitations.form.email', 'Email Address')} *
              </IonLabel>
              <IonInput
                type="email"
                value={formData.coachee_email}
                placeholder={t('invitations.form.emailPlaceholder', 'Enter email address')}
                onIonInput={(e) => setFormData(prev => ({ ...prev, coachee_email: e.detail.value! }))}
              />
            </IonItem>

            <IonItem className="rounded-lg">
              <IonLabel position="stacked">
                {t('invitations.form.maxUses', 'Maximum Uses')}
              </IonLabel>
              <IonSelect
                value={formData.max_uses}
                onIonChange={(e) => setFormData(prev => ({ ...prev, max_uses: e.detail.value }))}
              >
                <IonSelectOption value={1}>1 use</IonSelectOption>
                <IonSelectOption value={5}>5 uses</IonSelectOption>
                <IonSelectOption value={10}>10 uses</IonSelectOption>
              </IonSelect>
            </IonItem>

            <IonItem className="rounded-lg">
              <IonLabel position="stacked">
                {t('invitations.form.notes', 'Notes (Optional)')}
              </IonLabel>
              <IonTextarea
                value={formData.notes}
                placeholder={t('invitations.form.notesPlaceholder', 'Add any notes about this invitation...')}
                onIonInput={(e) => setFormData(prev => ({ ...prev, notes: e.detail.value! }))}
                rows={3}
              />
            </IonItem>

            <div className="mt-6">
              <IonButton
                expand="block"
                onClick={createInvitation}
                disabled={isCreatingInvitation}
              >
                {isCreatingInvitation ? (
                  <>
                    <IonSpinner slot="start" />
                    {t('invitations.creating', 'Creating...')}
                  </>
                ) : (
                  <>
                    <IonIcon icon={addOutline} slot="start" />
                    {t('invitations.createButton', 'Create Invitation')}
                  </>
                )}
              </IonButton>
            </div>

            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>{t('invitations.info.title', 'How it works:')}</strong><br />
                {t('invitations.info.description', 'The invitation will be created with a unique code. You can share this code or the QR code with your coachee. They can use it during registration to automatically link with you.')}
              </p>
            </div>
          </div>
        </IonContent>
      </IonModal>

      {/* Invitation Details Modal */}
      <IonModal isOpen={showDetailsModal} onDidDismiss={() => setShowDetailsModal(false)}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>
              {t('invitations.details.title', 'Invitation Details')}
            </IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowDetailsModal(false)}>
                <IonIcon icon={closeOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          {selectedInvitation && (
            <div className="space-y-6">
              {/* Coachee Information */}
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle className="text-base sm:text-lg flex items-center justify-between">
                    <div className="flex items-center">
                      <IonIcon icon={personOutline} className="mr-2 flex-shrink-0" />
                      {t('invitations.details.coacheeInfo', 'Coachee Information')}
                    </div>
                    {!isEditingDetails && selectedInvitation.status === 'pending' && (
                      <IonButton 
                        fill="clear" 
                        size="small"
                        onClick={() => setIsEditingDetails(true)}
                      >
                        <IonIcon icon={createOutline} slot="start" />
                        {t('invitations.details.edit', 'Edit')}
                      </IonButton>
                    )}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  {isEditingDetails ? (
                    <div className="space-y-4">
                      <IonItem className="rounded-lg">
                        <IonLabel position="stacked">{t('invitations.form.firstName', 'First Name')} *</IonLabel>
                        <IonInput
                          value={editDetailsData.coachee_first_name}
                          placeholder={t('invitations.form.firstNamePlaceholder', 'Enter first name')}
                          onIonInput={(e) => setEditDetailsData(prev => ({ ...prev, coachee_first_name: e.detail.value! }))}
                        />
                      </IonItem>

                      <IonItem className="rounded-lg">
                        <IonLabel position="stacked">{t('invitations.form.lastName', 'Last Name')} *</IonLabel>
                        <IonInput
                          value={editDetailsData.coachee_last_name}
                          placeholder={t('invitations.form.lastNamePlaceholder', 'Enter last name')}
                          onIonInput={(e) => setEditDetailsData(prev => ({ ...prev, coachee_last_name: e.detail.value! }))}
                        />
                      </IonItem>

                      <IonItem className="rounded-lg">
                        <IonLabel position="stacked">{t('invitations.form.email', 'Email Address')} *</IonLabel>
                        <IonInput
                          type="email"
                          value={editDetailsData.coachee_email}
                          placeholder={t('invitations.form.emailPlaceholder', 'Enter email address')}
                          onIonInput={(e) => setEditDetailsData(prev => ({ ...prev, coachee_email: e.detail.value! }))}
                        />
                      </IonItem>

                      <IonItem className="rounded-lg">
                        <IonLabel position="stacked">{t('invitations.form.notes', 'Notes')} ({t('common.optional', 'Optional')})</IonLabel>
                        <IonTextarea
                          value={editDetailsData.notes}
                          placeholder={t('invitations.form.notesPlaceholder', 'Add any notes about this invitation...')}
                          onIonInput={(e) => setEditDetailsData(prev => ({ ...prev, notes: e.detail.value! }))}
                          rows={3}
                        />
                      </IonItem>

                      <div className="flex gap-2 mt-4">
                        <IonButton
                          expand="block"
                          onClick={saveInvitationDetails}
                          disabled={isSavingDetails}
                        >
                          {isSavingDetails ? (
                            <>
                              <IonSpinner slot="start" />
                              {t('common.saving', 'Saving...')}
                            </>
                          ) : (
                            <>
                              <IonIcon icon={checkmarkCircleOutline} slot="start" />
                              {t('invitations.details.saveChanges', 'Save Changes')}
                            </>
                          )}
                        </IonButton>
                        <IonButton
                          expand="block"
                          fill="outline"
                          onClick={cancelEditDetails}
                          disabled={isSavingDetails}
                          className="hidden sm:block"
                        >
                          {t('common.cancel', 'Cancel')}
                        </IonButton>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Intended Coachee Information */}
                      <div className="space-y-2">
                        <h4 className="font-medium text-gray-700">{t('invitations.details.intendedCoachee', 'Intended For:')}</h4>
                        <p><strong>{t('invitations.details.name', 'Name')}:</strong> {selectedInvitation.coachee_first_name} {selectedInvitation.coachee_last_name}</p>
                        <p><strong>{t('invitations.details.email', 'Email')}:</strong> {selectedInvitation.coachee_email}</p>
                      </div>

                      {/* Actual Coachee Information (if accepted) */}
                      {selectedInvitation.status === 'accepted' && acceptedCoacheeInfo && (
                        <div className="space-y-2 p-3 bg-green-50 rounded-lg border border-green-200">
                          <h4 className="font-medium text-green-800 flex items-center">
                            <IonIcon icon={checkmarkCircleOutline} className="mr-2" />
                            {t('invitations.details.acceptedBy', 'Accepted By:')}
                          </h4>
                          <p><strong>{t('invitations.details.name', 'Name')}:</strong> {acceptedCoacheeInfo.full_name}</p>
                          <p><strong>{t('invitations.details.email', 'Email')}:</strong> {acceptedCoacheeInfo.email}</p>
                          {selectedInvitation.used_at && (
                            <p><strong>{t('invitations.details.acceptedAt', 'Accepted At')}:</strong> {new Date(selectedInvitation.used_at).toLocaleString()}</p>
                          )}
                        </div>
                      )}

                      {/* Status and Notes */}
                      <div className="space-y-2">
                        <p><strong>{t('invitations.details.status', 'Status')}:</strong> 
                          <IonChip color={getStatusColor(selectedInvitation.status)} className="ml-2">
                            <IonIcon icon={getStatusIcon(selectedInvitation.status)} />
                            <IonLabel>{selectedInvitation.status}</IonLabel>
                          </IonChip>
                        </p>
                        {selectedInvitation.notes && (
                          <p><strong>{t('invitations.details.notes', 'Notes')}:</strong> {selectedInvitation.notes}</p>
                        )}
                      </div>
                    </div>
                  )}
                </IonCardContent>
              </IonCard>

              {/* Invitation Code */}
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle className="text-base sm:text-lg flex items-center">
                    <IonIcon icon={linkOutline} className="mr-2 flex-shrink-0" />
                    {t('invitations.details.invitationCode', 'Invitation Code')}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <div className="bg-gray-100 p-4 rounded-lg mb-4">
                    <code className="text-lg font-mono">{selectedInvitation.code}</code>
                  </div>
                  <div className="flex gap-2">
                    <IonButton 
                      size="small" 
                      fill="outline"
                      onClick={() => copyToClipboard(selectedInvitation.code, 'Invitation code')}
                    >
                      <IonIcon icon={copyOutline} slot="start" />
                      {t('invitations.details.copyCode', 'Copy Code')}
                    </IonButton>
                    <IonButton 
                      size="small" 
                      fill="outline"
                      onClick={() => shareInvitation(selectedInvitation)}
                    >
                      <IonIcon icon={shareOutline} slot="start" />
                      {t('invitations.details.share', 'Share')}
                    </IonButton>
                  </div>
                </IonCardContent>
              </IonCard>

              {/* QR Code */}
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle className="text-base sm:text-lg flex items-center">
                    <IonIcon icon={qrCodeOutline} className="mr-2 flex-shrink-0" />
                    {t('invitations.details.qrCode', 'QR Code')}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <div className="text-center">
                    {qrCodeDataUrl ? (
                      <div>
                        <IonImg src={qrCodeDataUrl} alt="QR Code" className="mx-auto mb-4" style={{ maxWidth: '256px' }} />
                        <p className="text-sm text-gray-600 mb-4">
                          {t('invitations.details.qrCodeDescription', 'Coachees can scan this QR code to register directly')}
                        </p>
                        <IonButton 
                          size="small" 
                          fill="outline"
                          onClick={() => {
                            const link = document.createElement('a')
                            link.download = `invitation-${selectedInvitation.code}.png`
                            link.href = qrCodeDataUrl
                            link.click()
                          }}
                        >
                          {t('invitations.details.downloadQR', 'Download QR Code')}
                        </IonButton>
                      </div>
                    ) : (
                      <IonSpinner />
                    )}
                  </div>
                </IonCardContent>
              </IonCard>

              {/* Invitation Details */}
              <IonCard>
                <IonCardHeader>
                  <IonCardTitle className="text-base sm:text-lg flex items-center">
                    <IonIcon icon={listOutline} className="mr-2 flex-shrink-0" />
                    {t('invitations.details.invitationDetails', 'Invitation Details')}
                  </IonCardTitle>
                </IonCardHeader>
                <IonCardContent>
                  <div className="space-y-2">
                    <p><strong>{t('invitations.details.created', 'Created')}:</strong> {new Date(selectedInvitation.created_at).toLocaleString()}</p>
                    <p><strong>{t('invitations.details.expires', 'Expires')}:</strong> {new Date(selectedInvitation.expires_at).toLocaleString()}</p>
                    <p><strong>{t('invitations.details.uses', 'Uses')}:</strong> {selectedInvitation.current_uses} / {selectedInvitation.max_uses}</p>
                    {selectedInvitation.notes && (
                      <p><strong>{t('invitations.details.notes', 'Notes')}:</strong> {selectedInvitation.notes}</p>
                    )}
                  </div>
                </IonCardContent>
              </IonCard>

              {/* Actions */}
              {selectedInvitation.status === 'pending' && (
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle className="text-base sm:text-lg flex items-center">
                      <IonIcon icon={settingsOutline} className="mr-2 flex-shrink-0" />
                      {t('invitations.details.actions', 'Actions')}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <IonButton 
                      color="danger" 
                      fill="outline"
                      onClick={() => {
                        setInvitationToRevoke(selectedInvitation.id)
                        setShowRevokeAlert(true)
                      }}
                    >
                      <IonIcon icon={trashOutline} slot="start" />
                      {t('invitations.details.revoke', 'Revoke Invitation')}
                    </IonButton>
                  </IonCardContent>
                </IonCard>
              )}

              {/* Delete Action for Revoked Invitations */}
              {selectedInvitation.status === 'revoked' && (
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle className="text-base sm:text-lg flex items-center">
                      <IonIcon icon={settingsOutline} className="mr-2 flex-shrink-0" />
                      {t('invitations.details.actions', 'Actions')}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <IonButton 
                      color="danger" 
                      fill="outline"
                      onClick={() => {
                        setInvitationToDelete(selectedInvitation.id)
                        setShowDeleteAlert(true)
                      }}
                    >
                      <IonIcon icon={trashOutline} slot="start" />
                      {t('invitations.details.delete', 'Delete Invitation')}
                    </IonButton>
                    <p className="text-sm text-gray-600 mt-2">
                      {t('invitations.details.deleteWarning', 'This will permanently remove the invitation from your records.')}
                    </p>
                  </IonCardContent>
                </IonCard>
              )}
            </div>
          )}
        </IonContent>
      </IonModal>

      {/* Action Sheet */}
      <IonActionSheet
        isOpen={showActionSheet}
        onDidDismiss={() => setShowActionSheet(false)}
        buttons={[
          {
            text: t('invitations.actions.viewDetails', 'View Details'),
            icon: eyeOutline,
            handler: () => {
              if (actionSheetInvitation) {
                openInvitationDetails(actionSheetInvitation)
              }
            }
          },
          {
            text: t('invitations.actions.copyCode', 'Copy Code'),
            icon: copyOutline,
            handler: () => {
              if (actionSheetInvitation) {
                copyToClipboard(actionSheetInvitation.code, 'Invitation code')
              }
            }
          },
          {
            text: t('invitations.actions.share', 'Share'),
            icon: shareOutline,
            handler: () => {
              if (actionSheetInvitation) {
                shareInvitation(actionSheetInvitation)
              }
            }
          },
          ...(actionSheetInvitation?.status === 'pending' ? [{
            text: t('invitations.actions.revoke', 'Revoke'),
            icon: trashOutline,
            role: 'destructive' as const,
            handler: () => {
              if (actionSheetInvitation) {
                setInvitationToRevoke(actionSheetInvitation.id)
                setShowRevokeAlert(true)
              }
            }
          }] : []),
          ...(actionSheetInvitation?.status === 'revoked' ? [{
            text: t('invitations.actions.delete', 'Delete'),
            icon: trashOutline,
            role: 'destructive' as const,
            handler: () => {
              if (actionSheetInvitation) {
                setInvitationToDelete(actionSheetInvitation.id)
                setShowDeleteAlert(true)
              }
            }
          }] : []),
          {
            text: t('common.cancel', 'Cancel'),
            role: 'cancel'
          }
        ]}
      />

      {/* Revoke Alert */}
      <IonAlert
        isOpen={showRevokeAlert}
        onDidDismiss={() => setShowRevokeAlert(false)}
        header={t('invitations.revoke.title', 'Revoke Invitation')}
        message={t('invitations.revoke.message', 'Are you sure you want to revoke this invitation? The coachee will no longer be able to use this code to register.')}
        buttons={[
          {
            text: t('common.cancel', 'Cancel'),
            role: 'cancel'
          },
          {
            text: t('invitations.revoke.confirm', 'Revoke'),
            role: 'destructive',
            handler: revokeInvitation
          }
        ]}
      />

      {/* Delete Alert */}
      <IonAlert
        isOpen={showDeleteAlert}
        onDidDismiss={() => setShowDeleteAlert(false)}
        header={t('invitations.delete.title', 'Delete Invitation')}
        message={t('invitations.delete.message', 'Are you sure you want to permanently delete this invitation? This action cannot be undone and will remove all records of this invitation.')}
        buttons={[
          {
            text: t('common.cancel', 'Cancel'),
            role: 'cancel'
          },
          {
            text: t('invitations.delete.confirm', 'Delete'),
            role: 'destructive',
            handler: deleteInvitation
          }
        ]}
      />

      {/* Toast */}
      <IonToast
        isOpen={showToast}
        onDidDismiss={() => setShowToast(false)}
        message={toastMessage}
        duration={3000}
        position="bottom"
      />
    </>
  )
}

export default InvitationManagement 